import React from 'react';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { fetchPendingMaterialConfirmations, fetchMaterialConfirmationOrder } from '@/lib/api/materialOrders';
import { MaterialOrderReceipt } from './MaterialOrderReceipt';

export function PendingMaterialConfirmations() {
  const [pending, setPending] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [receipt, setReceipt] = React.useState(null);
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const request = React.useRef(0);
  const refresh = React.useCallback(async () => {
    const current = ++request.current;
    try {
      const data = await fetchPendingMaterialConfirmations();
      if (current === request.current) { setPending(data); setError(''); }
    } catch (e) { if (current === request.current) { setPending(null); setError(e.message); } }
  }, []);
  React.useEffect(() => {
    refresh();
    const update = () => { if (!document.hidden) refresh(); };
    const timer = window.setInterval(update, 30000);
    window.addEventListener('focus', update);
    window.addEventListener('material-confirmations-updated', update);
    return () => { ++request.current; window.clearInterval(timer); window.removeEventListener('focus', update); window.removeEventListener('material-confirmations-updated', update); };
  }, [refresh]);
  const review = async id => {
    setBusy(true); setError('');
    try { setReceipt(await fetchMaterialConfirmationOrder(id)); } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  return <>
    <Button type="button" variant="outline" className={pending?.length ? 'border-amber-300 bg-amber-50 text-amber-900' : ''} onClick={() => { setOpen(true); refresh(); }}>
      <Bell className="mr-2 h-4 w-4" /><span aria-live="polite">{pending === null ? 'Confirmaciones de materiales: sin datos' : `${pending.length} ${pending.length === 1 ? 'confirmación pendiente' : 'confirmaciones pendientes'} de visto bueno`}</span>
    </Button>
    <Dialog open={open && !receipt} onOpenChange={setOpen}><DialogContent className="max-w-3xl max-h-[90dvh] overflow-y-auto">
      <DialogTitle>Confirmaciones de materiales pendientes de visto bueno</DialogTitle>
      <DialogDescription>Revisa las cantidades enviadas por el proveedor y da el visto bueno. Las cantidades ya figuran en el cuadrante; esta revisión no registra una recepción.</DialogDescription>
      {error && <p role="alert" className="text-red-700">{error}</p>}
      <Button variant="outline" disabled={busy} onClick={refresh}>Actualizar avisos</Button>
      {pending?.length === 0 && <p>No hay confirmaciones pendientes de visto bueno.</p>}
      {pending?.map(row => <section key={row.id} className="rounded border p-3 space-y-2">
        <h3 className="font-semibold">{row.order_number} · {row.supplier_name}</h3>
        <p className="text-sm">{row.production_order_number} · Enviado: {new Date(row.submitted_at).toLocaleString('es-ES')}</p>
        <Button variant="outline" disabled={busy} onClick={() => review(row.id)}>{busy ? 'Cargando…' : 'Revisar confirmación'}</Button>
      </section>)}
    </DialogContent></Dialog>
    {receipt && <MaterialOrderReceipt receipt={receipt} onClose={() => { setReceipt(null); refresh(); }} onReload={async () => setReceipt(await fetchMaterialConfirmationOrder(receipt.id))} onReviewed={async () => { setReceipt(null); await refresh(); }} onUpdated={async () => { setReceipt(await fetchMaterialConfirmationOrder(receipt.id)); await refresh(); }} />}
  </>;
}
